import * as THREE from 'three/webgpu';
import React, { useCallback, useEffect, useRef } from 'react';
import { type ThreeElements, useFrame } from '@react-three/fiber';
import {
  useGLTF,
  useAnimations,
  MotionPathControls,
  useMotion,
} from '@react-three/drei';
import { type GLTF } from 'three/examples/jsm/Addons.js';
import pathJson from '../../../public/path/island.json';
import pathJsonTwo from '../../../public/path/butterfy_2.json';

import {
  createCurveFromJSON,
  type JsonPathType,
} from '../../utils/use-path-converter';

type ActionName = 'fly_action';

interface GLTFAction extends THREE.AnimationClip {
  name: ActionName;
}

type GLTFResult = GLTF & {
  nodes: {
    Circle002: THREE.SkinnedMesh;
    Circle002_1: THREE.SkinnedMesh;
    Base: THREE.Bone;
  };
  materials: {
    ['Butterfly.Body']: THREE.MeshStandardMaterial;
    ['Butterfly.Wings']: THREE.MeshPhysicalMaterial;
  };
  animations: GLTFAction[];
};

interface GLTFAction extends THREE.AnimationClip {
  name: ActionName;
}

type WoodPeakerGLTFResult = GLTF & {
  nodes: {
    Object_0: THREE.SkinnedMesh;
    Bone: THREE.Bone;
  };
  materials: {
    ['Material.001']: THREE.MeshStandardMaterial;
  };
  animations: GLTFAction[];
};

export function FlyingObjects(props: ThreeElements['group']) {
  const group = useRef<THREE.Group>(null!);
  const woodpeakerRef = useRef<THREE.Group>(null!);

  const { animations, nodes, materials } = useGLTF(
    '/models/butterfly_2-transformed.glb'
  ) as unknown as GLTFResult;

  const woodpeaker = useGLTF(
    '/models/woodpeaker-transformed.glb'
  ) as unknown as WoodPeakerGLTFResult;

  const { actions: woodpeaker_actions } = useAnimations(
    woodpeaker.animations,
    woodpeakerRef
  );

  const { actions } = useAnimations(animations, group);
  const getCurve = useCallback((pathData: JsonPathType) => {
    const { line3Curves } = createCurveFromJSON(pathData);
    return line3Curves;
  }, []);

  useEffect(() => {
    const action = actions['fly_action'];
    const woodpeaker_action = woodpeaker_actions['fly_action'];
    if (!action) return;
    if (!woodpeaker_action) return;

    // butterfly
    action.timeScale = 2;
    action.reset().fadeIn(0.2).play();

    // woodpeaker
    woodpeaker_action.timeScale = 4;
    woodpeaker_action.reset().fadeIn(0.2).play();
    return () => {
      action.fadeOut(0.2);
      woodpeaker_action.fadeOut(0.2);
    };
  }, [actions, woodpeaker_actions]);

  return (
    <>
      <MotionPathControls
        curves={getCurve(pathJsonTwo)}
        debug={false}
        debugColor={'red'}
        object={group}
        smooth={true}
        loop={true}
      >
        <Loop factor={0.1} ref={group} />
      </MotionPathControls>

      <group ref={group} {...props} name='a' scale={3}>
        <primitive object={nodes.Base} />
        <group name='Butterfly2'>
          <skinnedMesh
            name='Circle002'
            geometry={nodes.Circle002.geometry}
            material={materials['Butterfly.Body']}
            skeleton={nodes.Circle002.skeleton}
          />
          <skinnedMesh
            name='Circle002_1'
            geometry={nodes.Circle002_1.geometry}
            material={materials['Butterfly.Wings']}
            skeleton={nodes.Circle002_1.skeleton}
          />
        </group>
      </group>

      <MotionPathControls
        curves={getCurve(pathJson)}
        debug={false}
        debugColor={'blue'}
        object={woodpeakerRef}
        smooth={true}
      >
        <Loop factor={0.1} ref={woodpeakerRef} />
      </MotionPathControls>
      <group ref={woodpeakerRef} {...props} dispose={null}>
        <group name='Scene'>
          <group name='woodpecker'>
            <primitive object={woodpeaker.nodes.Bone} />
          </group>
          <skinnedMesh
            name='Object_0'
            geometry={woodpeaker.nodes.Object_0.geometry}
            material={woodpeaker.materials['Material.001']}
            skeleton={woodpeaker.nodes.Object_0.skeleton}
            morphTargetDictionary={
              woodpeaker.nodes.Object_0.morphTargetDictionary
            }
            morphTargetInfluences={
              woodpeaker.nodes.Object_0.morphTargetInfluences
            }
          />
        </group>
      </group>

      {/* <MotionPathControls
        curves={getCurve(pathJsonTwo)}
        debug={true}
        debugColor={'blue'}
        object={group2}
        smooth={true}
      >
        <Loop factor={0.02} ref={group2} />
      </MotionPathControls>

      <group ref={group2} {...props} scale={3}>
        <primitive object={nodes.Base} />
        <group>
          <skinnedMesh
            name='Circle002'
            geometry={nodes.Circle002.geometry}
            material={materials['Butterfly.Body']}
            skeleton={nodes.Circle002.skeleton}
          />
          <skinnedMesh
            name='Circle002_1'
            geometry={nodes.Circle002_1.geometry}
            material={materials['Butterfly.Wings']}
            skeleton={nodes.Circle002_1.skeleton}
          />
        </group>
      </group> */}
    </>
  );
}

function Loop({
  factor = 0.02,
  ref,
}: {
  factor: number;
  ref: React.RefObject<THREE.Group<THREE.Object3DEventMap>>;
}) {
  const motion = useMotion();

  useFrame((_state, delta) => {
    motion.current += Math.min(0.1, delta) * factor;

    // Look ahead on the curve
    ref.current.lookAt(motion.next);
  });
  return null;
}

useGLTF.preload('/models/butterfly_2-transformed.glb');
useGLTF.preload('/models/woodpeaker-transformed.glb');
